/**
 * Amplify Auth configuration — Task 4.0a / Req 1.
 *
 * The Cognito coordinates come from build-time env vars (operator sets them
 * in the Amplify console: `VITE_COGNITO_DOMAIN`, `VITE_COGNITO_CLIENT_ID`,
 * `VITE_COGNITO_USER_POOL_ID`, `VITE_COGNITO_REDIRECT_URI`).
 *
 * We configure Amplify once at module import — calling configure() multiple
 * times is harmless but wasteful. main.tsx imports this for its side
 * effect before rendering.
 *
 * Task 1.1a: sign-in goes through Cognito **Managed Login** (the branded
 * Hosted UI). Calling `signInWithRedirect()` with no provider lands the user
 * on the Managed Login chooser, which presents BOTH Google and the native
 * email/password form (the User Pool client enables the Google + Cognito
 * providers). We don't use Amplify's direct username/password APIs — Managed
 * Login owns sign-up, verification, and reset — so `loginWith.username/email`
 * stay false; only the oauth (hosted) config matters here.
 */

import { Amplify } from "aws-amplify";

const region = "us-east-1";

const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN || "";
const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID || "";
const userPoolClientId = import.meta.env.VITE_COGNITO_CLIENT_ID || "";
const redirectUri =
  import.meta.env.VITE_COGNITO_REDIRECT_URI ||
  `${window.location.origin}/auth/callback`;
const signOutUri =
  import.meta.env.VITE_COGNITO_SIGN_OUT_URI ||
  `${window.location.origin}/`;

// Full Cognito Managed Login host. VITE_COGNITO_DOMAIN may be the bare prefix
// (e.g. "pricetilt-dev") or the full host; accept both.
const cognitoDomainHost = cognitoDomain.includes(".")
  ? cognitoDomain
  : `${cognitoDomain}.auth.${region}.amazoncognito.com`;

if (!cognitoDomain || !userPoolId || !userPoolClientId) {
  // Fail loud during dev — silent misconfig produces 'user undefined' bugs
  // that are very confusing to debug. Build-time env vars must be set.
  console.error(
    "Cognito env vars missing. Set VITE_COGNITO_DOMAIN, " +
      "VITE_COGNITO_USER_POOL_ID, and VITE_COGNITO_CLIENT_ID at build time.",
  );
}

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId,
      userPoolClientId,
      loginWith: {
        oauth: {
          // The Cognito-managed domain — operator owns the full host
          // (e.g., "pricetilt-dev.auth.us-east-1.amazoncognito.com").
          // VITE_COGNITO_DOMAIN may be either the bare prefix or the
          // full host; we accept both.
          domain: cognitoDomainHost,
          scopes: ["openid", "email", "profile"],
          redirectSignIn: [redirectUri],
          redirectSignOut: [signOutUri],
          responseType: "code",
          providers: ["Google"],
        },
        // Direct (non-hosted) username/password APIs stay off — Managed Login
        // handles email/password in the hosted page, not in-app forms.
        username: false,
        email: false,
      },
    },
  },
});

export const COGNITO_REDIRECT_URI = redirectUri;
export const COGNITO_SIGN_OUT_URI = signOutUri;

// Cognito hosted logout endpoint. Navigating here clears the Managed Login
// session cookie (a plain `signOut()` clears only the SPA's local tokens, so
// the hosted session survives and silently re-authenticates on the next
// authorize request). Cognito redirects to `logout_uri` (the marketing site)
// when it's registered in the client's allowed logout URLs.
export const COGNITO_LOGOUT_URL =
  `https://${cognitoDomainHost}/logout` +
  `?client_id=${encodeURIComponent(userPoolClientId)}` +
  `&logout_uri=${encodeURIComponent(signOutUri)}`;
